import configs from '@/config'
import quizScenario from './scenarios/quiz.json'
import ldManagerScenario from './scenarios/ld-manager.json'
import firstPresentScenario from './scenarios/first-present.json'
import homeCreateFirstScenario from './scenarios/home-create-first.json'

/**
 * Idle sentinel — the value of OnboardingOverlay's `scenarioId` when NO tour is
 * running. Deliberately a non-empty string that is NOT a SCENARIO_REGISTRY key, so
 * it can never be loaded as a scenario.
 */
export const IDLE_SCENARIO = '__idle__'

/**
 * Registry maps scenario IDs to their local config objects.
 * To add a new built-in scenario: import its JSON and add an entry here.
 */
const SCENARIO_REGISTRY = {
  quiz: quizScenario,
  'ld-manager': ldManagerScenario,
  'first-present': firstPresentScenario,
  'home-create-first': homeCreateFirstScenario,
}

/** In-memory cache for fetched remote scenarios. */
const remoteCache = {}

/**
 * Converts a glob-style path pattern (with * wildcards) to a regex and tests against currentPath.
 * Example: "/presentation/*" matches "/presentation/abc123"
 */
export function matchPath(pattern, currentPath) {
  const escaped = pattern.replace(/[-[\]{}()+?.,\\^$|#\s]/g, '\\$&').replace(/\*/g, '[^/]+')
  return new RegExp(`^${escaped}$`).test(currentPath)
}

/**
 * Returns true if currentPath matches any pattern in patternOrPatterns.
 * Accepts either a single pattern string or an array of pattern strings,
 * allowing a step to be visible across multiple routes.
 *
 * @param {string|string[]} patternOrPatterns
 * @param {string} currentPath
 */
export function matchPathAny(patternOrPatterns, currentPath) {
  const patterns = Array.isArray(patternOrPatterns) ? patternOrPatterns : [patternOrPatterns]
  return patterns.some((p) => matchPath(p, currentPath))
}

/**
 * Returns the first exact (non-wildcard) path from a step's path field.
 * Used to determine where to navigate when a step becomes active.
 * Returns null if all patterns contain wildcards (user must navigate there naturally).
 *
 * @param {string|string[]} patternOrPatterns
 * @returns {string|null}
 */
export function firstExactPath(patternOrPatterns) {
  const patterns = Array.isArray(patternOrPatterns) ? patternOrPatterns : [patternOrPatterns]
  return patterns.find((p) => !p.includes('*')) ?? null
}

/**
 * Resolves a selector expression to a standard CSS selector string.
 *
 * Supported prefixes:
 *   data-testid=value  →  [data-testid="value"]
 *   id=value           →  #value
 *   name=value         →  [name="value"]
 *   (anything else)    →  passed through as-is (raw CSS selector)
 */
export function resolveSelector(expression) {
  if (expression.startsWith('data-testid=')) {
    return `[data-testid="${expression.slice('data-testid='.length)}"]`
  }
  if (expression.startsWith('id=')) {
    return `#${expression.slice('id='.length)}`
  }
  if (expression.startsWith('name=')) {
    return `[name="${expression.slice('name='.length)}"]`
  }
  return expression
}

/**
 * Returns localStorage key names scoped to a given scenario ID.
 * Per-scenario keys prevent cross-scenario state leakage when multiple
 * scenarios are in use (e.g. via URL param).
 *
 * @param {string|undefined} scenarioId
 * @returns {{ step: string, dismissed: string }}
 */
export function storageKeys(scenarioId) {
  const id = scenarioId || IDLE_SCENARIO
  return {
    step: `aha_onboarding_v1_${id}_step`,
    dismissed: `aha_onboarding_v1_${id}_dismissed`,
  }
}

/**
 * localStorage key holding a one-shot "pending scenario" request.
 *
 * Some scenarios must auto-launch in response to an event that happens on a
 * different route than where the scenario starts (e.g. the first-present tour is
 * armed when a presentation is created from onboarding, but its first step anchors
 * on the editor that loads afterward). The arming code writes the scenario id here;
 * the overlay reads + clears it on its next init so the tour runs exactly once.
 */
export const PENDING_SCENARIO_KEY = 'aha_onboarding_v1_pending_scenario'

/**
 * Window event name dispatched by armPendingScenario() right after the one-shot
 * marker is written. The OnboardingOverlay listens for it so a scenario armed on
 * the SAME route the overlay is already mounted on (e.g. home-create-first armed
 * in Home.vue's mounted() AFTER the overlay's created()/init already resolved to
 * the IDLE sentinel) is consumed immediately, instead of waiting for the next route change
 * that may never come. The overlay still re-enters _initScenario(), so every gate
 * (release flag + per-scenario variant) is re-applied — this only re-opens the
 * consume path, it never bypasses gating. (BOO-63 round 4.)
 */
export const ONBOARDING_SCENARIO_ARMED_EVENT = 'aha-onboarding:scenario-armed'

/**
 * sessionStorage key that persists the ?onboarding= URL override across Vue Router
 * redirects. Vue Router drops query params during client-side redirects (e.g. / →
 * /presentations → /presentation/:id). By capturing the param into sessionStorage
 * at the very first beforeEach call — before any redirect fires — we ensure the
 * override is still readable when OnboardingOverlay initialises on the final route.
 *
 * sessionStorage scope (tab-lifetime) is intentional: it acts exactly like having
 * the ?onboarding= param in the URL for the current tab session, without polluting
 * subsequent tabs or after-restart loads.
 */
export const ONBOARDING_URL_OVERRIDE_KEY = 'aha_onboarding_v1_url_override'

/**
 * Captures the ?onboarding= query param from a URL search string into sessionStorage.
 * Call this in the router's first beforeEach guard, BEFORE any redirect happens,
 * so the value survives the full redirect chain.
 *
 * No-op when the param is absent — does not clear an existing captured value, so
 * a previous call's capture is preserved through the redirect.
 *
 * @param {string} search  — e.g. "?onboarding=first-present&foo=bar" or "" if absent
 */
export function captureOnboardingUrlOverride(search) {
  try {
    const val = new URLSearchParams(search).get('onboarding')
    if (val) {
      sessionStorage.setItem(ONBOARDING_URL_OVERRIDE_KEY, val)
    }
  } catch (e) {
    // sessionStorage unavailable (private mode, cross-origin iframe) — best-effort.
  }
}

/**
 * Returns the captured ?onboarding= override value, or null if none was captured.
 * Does NOT clear the value; call clearOnboardingUrlOverride() when the overlay
 * has consumed it (to prevent it from re-triggering after a manual page reload).
 *
 * @returns {string|null}
 */
export function readOnboardingUrlOverride() {
  try {
    return sessionStorage.getItem(ONBOARDING_URL_OVERRIDE_KEY)
  } catch (e) {
    return null
  }
}

/**
 * Clears the sessionStorage override flag. Call this once the overlay has
 * successfully initialised a scenario from the URL override, so that a hard
 * reload of the same URL (without ?onboarding=) does not re-trigger the tour.
 */
export function clearOnboardingUrlOverride() {
  try {
    sessionStorage.removeItem(ONBOARDING_URL_OVERRIDE_KEY)
  } catch (e) {
    // best-effort
  }
}

/**
 * Arms a scenario to auto-launch on the overlay's next initialization.
 * One-shot: consumed (and cleared) by consumePendingScenario().
 *
 * @param {string} scenarioId — a SCENARIO_REGISTRY id
 */
export function armPendingScenario(scenarioId) {
  try {
    localStorage.setItem(PENDING_SCENARIO_KEY, scenarioId)
  } catch (e) {
    // localStorage can throw in private-mode / quota-exceeded; arming is best-effort.
    // eslint-disable-next-line no-console
    console.error('[onboarding] failed to arm pending scenario:', e)
  }

  // Notify a mounted OnboardingOverlay that a marker was just armed, so it can
  // consume it on the SAME route without waiting for a route change. Dispatched
  // even when the localStorage write threw — the overlay re-reads the marker via
  // its own guarded peek and simply no-ops if nothing is pending. Best-effort:
  // `window` is undefined in SSR/unit contexts, and dispatchEvent must never break
  // the (non-blocking, best-effort) arm path. (BOO-63 round 4.)
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent(ONBOARDING_SCENARIO_ARMED_EVENT, {
        detail: { scenarioId },
      }))
    }
  } catch (e) {
    // Ignore — overlay also re-inits on route change as a fallback.
  }
}

// Prefix every onboarding deep-link origin carries in its `deepLinkSource` query
// param (e.g. 'onboarding-ikea-checklist', 'onboarding-use-case',
// 'onboarding-no-question', 'onboarding-profession'). This is the GENERIC origin
// signal: the onboarding app only declares "this presentation came from
// onboarding", and the presenter owns whatever it decides to do with that fact.
export const ONBOARDING_ORIGIN_PREFIX = 'onboarding-'

// Scenario id of the guided "first present" tour (Present → QR → Start) and the
// Unleash flag/variant that gates it. The presenter — not the onboarding app —
// owns this gate, so future onboarding-triggered behaviours can reuse the same
// generic origin signal without any onboarding-side change.
export const FIRST_PRESENT_TOUR_SCENARIO = 'first-present'
export const FIRST_PRESENT_DEMO_FLAG = 'AHA-42601-present-demo-tour'
export const FIRST_PRESENT_DEMO_VARIANT = 'present-demo'

// BOO-63 / AHA-43208: guided "home → create first presentation → present" tour for
// the skipped-onboarding cohort. It SUPERSEDES the BOO-30 first-present tour for
// users who skipped onboarding (those users see this tour; non-skip users still see
// BOO-30). Armed on Home load (the funnel opens after the user clicks "New
// presentation"). Gated by its own Unleash variant, same getVariant(...).name
// convention as the first-present gate above.
export const HOME_CREATE_FIRST_TOUR_SCENARIO = 'home-create-first'
export const HOME_CREATE_FIRST_FLAG = 'AHA-43208-tour-for-skip-ob'
export const HOME_CREATE_FIRST_VARIANT = 'tour-on-for-skip'

/**
 * Per-scenario Unleash gate map: scenario id → { flag, variant }. A scenario listed
 * here only renders when getVariant(flag).name === variant. Scenarios NOT listed
 * (quiz, ld-manager) have no variant gate and are reachable only via the
 * ?onboarding= QA override. Keeping this declarative in one place lets the overlay
 * engine gate any number
 * of variant-driven tours without per-scenario branching.
 */
export const SCENARIO_VARIANT_GATES = {
  [FIRST_PRESENT_TOUR_SCENARIO]: {
    flag: FIRST_PRESENT_DEMO_FLAG,
    variant: FIRST_PRESENT_DEMO_VARIANT,
  },
  [HOME_CREATE_FIRST_TOUR_SCENARIO]: {
    flag: HOME_CREATE_FIRST_FLAG,
    variant: HOME_CREATE_FIRST_VARIANT,
  },
}

/**
 * Returns the { flag, variant } gate for a scenario id, or null when the scenario
 * is not variant-gated. Used by the overlay engine to decide whether a scenario is
 * allowed to render under the current Unleash assignment.
 *
 * @param {string} scenarioId
 * @returns {{ flag: string, variant: string } | null}
 */
export function getScenarioVariantGate(scenarioId) {
  return SCENARIO_VARIANT_GATES[scenarioId] || null
}

/**
 * True when the user explicitly SKIPPED onboarding — the cohort this tour targets.
 *
 * Two distinct skip signals exist because there are two onboarding implementations:
 *   - `personalInformation.skipUserScreening === true` — the LEGACY in-presenter
 *     UserScreening modal sets this when the user opts out (UserScreening.vue /
 *     UserScreening2025.vue).
 *   - `personalInformation.onboardingSkipped === true` — the NEW separate
 *     aha-onboarding app (AHA-42093, embedded via the Zoid iframe) sets this on a
 *     Skip-completion. Its shell `completeAndExit()` patches the user with
 *     `{ hasOnboarding, onboardingCompletedAt, onboardingSkipped: true, ... }`;
 *     it does NOT write `skipUserScreening`. (AHA-43207 introduced this field.)
 *
 * BOO-63 round 3: the user's repro goes through the NEW app
 * (`?onboardingSandbox=…&onboardingVariant=profession`), so only `onboardingSkipped`
 * is set after Skip — recognising the legacy field alone meant the tour never armed
 * for the very cohort it targets. Accept EITHER signal so the skipped cohort is
 * detected regardless of which onboarding implementation served them. The non-skip
 * cohort (who completed onboarding) is excluded here and continues to see the BOO-30
 * first-present tour.
 *
 * @param {object} [currentUser]
 * @returns {boolean}
 */
export function hasSkippedOnboarding(currentUser) {
  const pi = currentUser?.personalInformation
  return pi?.skipUserScreening === true || pi?.onboardingSkipped === true
}

/**
 * Arms the guided home-create-first tour for the skipped-onboarding cohort, IF and
 * only if every gate passes:
 *   1. the user skipped onboarding (`skipUserScreening === true`), AND
 *   2. the user has not yet created a presentation (`hasCreatedPresentation !== true`)
 *      — the tour walks first-presentation creation, so it is pointless for users who
 *      already have one, AND
 *   3. the Unleash variant `AHA-43208-tour-for-skip-ob` resolves to `tour-on-for-skip`.
 *
 * Called on Home load. Per Annie's spec the funnel (create flow) opens after the user
 * clicks "New presentation"; arming on Home load means the one-shot marker is in place
 * before the click so the tour's first step anchors the Home "New presentation" button
 * and the subsequent steps survive the Home → editor route change. Best-effort — never
 * throws (armPendingScenario swallows storage errors).
 *
 * @param {object}  args
 * @param {object} [args.currentUser]        the current user object
 * @param {string} [args.skipTourVariant]    resolved name of AHA-43208-tour-for-skip-ob
 * @returns {boolean} true when the tour was armed
 */
export function maybeArmHomeCreateFirstTour({
  currentUser,
  skipTourVariant,
} = {}) {
  const isVariantEnabled = skipTourVariant === HOME_CREATE_FIRST_VARIANT
  const isFirstEver = currentUser?.personalInformation?.hasCreatedPresentation !== true
  if (hasSkippedOnboarding(currentUser) && isFirstEver && isVariantEnabled) {
    armPendingScenario(HOME_CREATE_FIRST_TOUR_SCENARIO)
    return true
  }
  return false
}

/**
 * True when a `deepLinkSource` value indicates the presentation was created from
 * any onboarding flow. Generic by design: matches the shared `onboarding-` origin
 * prefix rather than any single feature, so it generalizes to future flows.
 *
 * @param {string} [deepLinkSource]
 * @returns {boolean}
 */
export function isOnboardingOrigin(deepLinkSource) {
  return typeof deepLinkSource === 'string'
    && deepLinkSource.startsWith(ONBOARDING_ORIGIN_PREFIX)
}

/**
 * Arms the guided first-present tour for a presentation that was just created
 * from onboarding — IF and only if every gate passes:
 *   1. the origin is an onboarding flow (`deepLinkSource` starts with `onboarding-`),
 *   2. the Unleash variant `AHA-42601-present-demo-tour` resolves to `present-demo`.
 *
 * BOO-39 (round 13): gate 1 (onboarding-origin) already implies first-ever
 * presentation. The onboarding app is blocked for users with any legacy-onboarding
 * signal (`hasLegacyOnboardingSignal` — which includes `hasCreatedPresentation`),
 * so a user who reaches an `onboarding-*` create is always a brand-new user.
 * An explicit backend presentation-count check is therefore redundant and not
 * worth the API cost.
 *
 * BOO-63 / AHA-43208 (cohort split): the skipped-onboarding cohort
 * (`skipUserScreening === true`) is now served by the home-create-first tour and must
 * NOT also get the first-present tour. In practice a skipped-onboarding user never
 * carries an `onboarding-*` deepLinkSource (they bypassed the onboarding flow that
 * stamps that origin), so this is already mutually exclusive — but we add an explicit
 * skipped-cohort exclusion here so the split is guaranteed even if a skipped user
 * re-enters an onboarding-origin create. Pass `currentUser` to activate the guard;
 * when omitted (legacy callers) behaviour is unchanged.
 *
 * The arm writes a one-shot localStorage marker consumed by OnboardingOverlay on
 * its next init, so it survives the deep-link → editor route change. Arming is
 * best-effort and never throws — callers run this in the create-landing happy path.
 *
 * @param {object}  args
 * @param {string} [args.deepLinkSource]      origin marker from the deep link
 * @param {string} [args.presentDemoVariant]  resolved name of AHA-42601-present-demo-tour
 * @param {object} [args.currentUser]         current user — used to exclude the
 *                                            skipped-onboarding cohort (BOO-63)
 * @returns {boolean} true when the tour was armed
 */
export function maybeArmFirstPresentTour({
  deepLinkSource,
  presentDemoVariant,
  currentUser,
} = {}) {
  const isVariantEnabled = presentDemoVariant === FIRST_PRESENT_DEMO_VARIANT
  // BOO-63: the skipped-onboarding cohort is served by home-create-first instead.
  if (currentUser !== undefined && hasSkippedOnboarding(currentUser)) {
    return false
  }
  if (isOnboardingOrigin(deepLinkSource) && isVariantEnabled) {
    armPendingScenario(FIRST_PRESENT_TOUR_SCENARIO)
    return true
  }
  return false
}

/**
 * Reads and clears the pending-scenario marker. Returns the armed scenario id,
 * or null when nothing is pending. Clearing on read guarantees the tour fires
 * once and does not re-trigger on subsequent page loads.
 *
 * Also clears the armed scenario's own step/dismissed state so it always launches
 * fresh from step 0 — even if the user happened to run (and dismiss) it before.
 *
 * @returns {string|null}
 */
export function consumePendingScenario() {
  try {
    const id = localStorage.getItem(PENDING_SCENARIO_KEY)
    if (!id) return null
    localStorage.removeItem(PENDING_SCENARIO_KEY)
    const keys = storageKeys(id)
    localStorage.removeItem(keys.step)
    localStorage.removeItem(keys.dismissed)
    return id
  } catch (e) {
    return null
  }
}

/**
 * Loads a scenario by ID or URL.
 *
 * - Local IDs are resolved from SCENARIO_REGISTRY. Returns null for unknown IDs —
 *   no fallback to 'default'. The caller must treat null as "do not launch".
 * - Full URLs (http/https) are fetched from the network and cached in memory,
 *   so they can be hosted anywhere without a redeploy.
 *
 * SECURITY: remote-URL scenarios are a QA-only convenience. A `?onboarding=<url>`
 * link opens the gate for ANY visitor and the fetched JSON drives DOM querying and
 * (for click-or-next steps) programmatic clicks on the victim's authenticated UI —
 * a crafted-link / deceptive-tour vector. So remote fetch is DISABLED in production:
 * there it throws so no scenario runs. ID-based registry scenarios
 * (quiz/ld-manager/first-present) work in every environment.
 *
 * @param {string|undefined} scenarioIdOrUrl
 * @returns {Promise<{ steps: Array } | null>}
 */
export async function loadScenario(scenarioIdOrUrl) {
  // Empty/falsy id means nothing explicitly requested a scenario — do not launch.
  // (Unknown registry ids also return null below; this closes the empty-id path.)
  if (!scenarioIdOrUrl) return null
  const key = scenarioIdOrUrl

  // Remote URL — fetch once and cache. Non-production only (see SECURITY above).
  const isRemoteUrl = key.startsWith('http://') || key.startsWith('https://')
  if (isRemoteUrl && configs.env !== 'production') {
    if (remoteCache[key]) return remoteCache[key]
    const res = await fetch(key)
    if (!res.ok) throw new Error(`Failed to fetch onboarding scenario from ${key}: HTTP ${res.status}`)
    const body = await res.json()
    // Some hosting services wrap the payload in an envelope (e.g. { record: {...} }).
    // If the response doesn't have a top-level `steps` array, unwrap the first
    // object-valued property whose value contains `steps`.
    const scenario = body && Array.isArray(body.steps)
      ? body
      : (body && typeof body === 'object'
        ? Object.values(body).find((v) => v && Array.isArray(v.steps))
        : null) ?? body
    remoteCache[key] = scenario
    return scenario
  }

  // Local registry — returns null for unknown IDs (no default fallback).
  // A missing scenario means nothing requested this ID explicitly; the caller
  // must treat null as "do not launch" and abort scenario initialisation.
  return SCENARIO_REGISTRY[key] ?? null
}

/** For testing only — clears the in-memory remote scenario cache between test runs. */
export function _clearRemoteCache() {
  Object.keys(remoteCache).forEach((k) => delete remoteCache[k])
}

/**
 * Replaces `$variable` placeholders in a string with values from the provided map.
 *
 * Supported placeholders:
 *   $name   — the user's display name (first name, or full name)
 *
 * Unknown placeholders are left as-is so future additions don't break old scenarios.
 *
 * @param {string} text
 * @param {Record<string, string>} vars  e.g. { name: 'Alice' }
 * @returns {string}
 */
export function interpolateText(text, vars) {
  if (!text || !vars) return text
  return text.replace(/\$([a-zA-Z_]\w*)/g, (match, key) => {
    return Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match
  })
}
